import React from 'react';
import styles from './EditorCampos.module.css';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import CampoMultiTexto from '@/components/Formulario/CampoMultiTexto';
import Grid, { GridItem } from '@/components/layout/Grid';
import { mdiPlusCircleOutline, mdiDelete } from '@mdi/js';
import {
    CampoDataset,
    TipoCampoDataset,
    tiposCampoDataset,
} from '@tera/shared/src/types/Dataset';

const tiposOptions = tiposCampoDataset.map((it) => ({
    id: it.id,
    label: <span>{it.label}</span>,
}));

const formatoOptions = [
    { id: 'texto', label: <span>Nenhum</span> },
    { id: 'moeda', label: <span>Moeda</span> },
    { id: 'percentual', label: <span>Percentual</span> },
];

/** Sugere uma chave estável a partir do rótulo digitado. */
export function sugerirChave(rotulo: string): string {
    return rotulo
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/^([0-9])/, 'c$1');
}

type Props = {
    value: CampoDataset[];
    onChange: (campos: CampoDataset[]) => void;
    fieldErrors?: Record<string, string>;
};

/**
 * Editor dos campos de uma tabela de dados.
 *
 * Diferente do editor de estrutura de dados: aqui a `chave` é a identidade do
 * campo (existem registros gravados apontando para ela), então ela é fixada na
 * criação e o rótulo é livre para renomear.
 */
const EditorCampos = ({ value, onChange, fieldErrors }: Props) => {
    // Chaves que já existiam ao abrir o editor: elas identificam registros
    // gravados, então nunca podem ser reescritas ao renomear o rótulo.
    // Depende de o componente ser remontado por dataset (ver `key` em FormDataset).
    const chavesPersistidas = React.useRef<Set<string> | null>(null);
    if (chavesPersistidas.current === null) {
        chavesPersistidas.current = new Set(
            value.filter((c) => c.chave).map((c) => c.chave),
        );
    }

    /**
     * A chave acompanha o rótulo enquanto ainda for a sugestão automática.
     * Assim que o usuário edita a chave à mão, ela para de ser sobrescrita.
     */
    const chaveAcompanhaRotulo = (campo: CampoDataset) =>
        !chavesPersistidas.current!.has(campo.chave) &&
        (!campo.chave || campo.chave === sugerirChave(campo.rotulo ?? ''));

    const atualizar = (indice: number, campo: CampoDataset) =>
        onChange([
            ...value.slice(0, indice),
            campo,
            ...value.slice(indice + 1),
        ]);

    const visiveis = value
        .map((campo, indice) => ({ campo, indice }))
        .filter(({ campo }) => !campo.removido_em);

    return (
        <div data-testId="editor-campos">
            {visiveis.map(({ campo, indice }) => (
                <div
                    data-testId={`campo-container-${indice}`}
                    // A key precisa ser estável: se derivar da chave, preencher a
                    // chave enquanto o rótulo é digitado remonta a linha e o campo
                    // perde o foco no meio da digitação.
                    key={`campo-${indice}`}
                    className={styles.campoContainer}
                >
                    <div className={styles.campoHeader}>
                        <HBox gap={8} stretch style={{ width: '100%' }}>
                            <Grid style={{ width: '100%' }}>
                                <GridItem sm={12} md={4}>
                                    <CampoTexto
                                        instanceId={`campo-rotulo-${indice}`}
                                        label="Rótulo"
                                        value={campo.rotulo ?? ''}
                                        onChange={(v) =>
                                            atualizar(indice, {
                                                ...campo,
                                                rotulo: v,
                                                chave: chaveAcompanhaRotulo(campo)
                                                    ? sugerirChave(v)
                                                    : campo.chave,
                                            })
                                        }
                                        error={fieldErrors?.[`campos[${indice}].rotulo`]}
                                    />
                                </GridItem>
                                <GridItem sm={12} md={3}>
                                    <CampoTexto
                                        instanceId={`campo-chave-${indice}`}
                                        label="Chave"
                                        value={campo.chave ?? ''}
                                        // Chave já persistida é a identidade dos registros
                                        // gravados: alterá-la faria o backend ler o campo como
                                        // removido + um novo, sumindo com os valores.
                                        disabled={chavesPersistidas.current!.has(campo.chave)}
                                        onChange={(v) =>
                                            atualizar(indice, { ...campo, chave: sugerirChave(v) })
                                        }
                                        error={fieldErrors?.[`campos[${indice}].chave`]}
                                    />
                                </GridItem>
                                <GridItem sm={12} md={3}>
                                    <CampoSeletor
                                        instanceId={`campo-tipo-${indice}`}
                                        options={tiposOptions}
                                        label="Tipo"
                                        value={campo.tipo}
                                        onChange={(v: TipoCampoDataset) =>
                                            atualizar(indice, { ...campo, tipo: v })
                                        }
                                        error={fieldErrors?.[`campos[${indice}].tipo`]}
                                    />
                                </GridItem>
                                <GridItem sm={12} md={2}>
                                    {campo.tipo === 'number' && (
                                        <CampoSeletor
                                            instanceId={`campo-formato-${indice}`}
                                            options={formatoOptions}
                                            label="Formato"
                                            value={campo.formato ?? 'texto'}
                                            onChange={(v: any) =>
                                                atualizar(indice, { ...campo, formato: v })
                                            }
                                        />
                                    )}
                                </GridItem>

                                <GridItem sm={12} md={8}>
                                    {campo.tipo === 'string' && (
                                        <CampoMultiTexto
                                            label="Valores permitidos (opcional)"
                                            value={campo.valores ?? []}
                                            onChange={(v: string[]) =>
                                                atualizar(indice, {
                                                    ...campo,
                                                    valores: v.length ? v : undefined,
                                                })
                                            }
                                        />
                                    )}
                                </GridItem>
                                <GridItem sm={12} md={4}>
                                    <HBox gap={16} style={{ flexWrap: 'wrap' }}>
                                        <CampoCheckbox
                                            instanceId={`campo-obrigatorio-${indice}`}
                                            label="Obrigatório"
                                            value={!!campo.obrigatorio}
                                            onChange={(v) =>
                                                atualizar(indice, { ...campo, obrigatorio: v })
                                            }
                                        />
                                        <CampoCheckbox
                                            instanceId={`campo-buscavel-${indice}`}
                                            label="Buscável"
                                            value={!!campo.buscavel}
                                            onChange={(v) =>
                                                atualizar(indice, { ...campo, buscavel: v })
                                            }
                                        />
                                    </HBox>
                                </GridItem>
                            </Grid>
                            <Botao
                                variant="none-danger"
                                title="Remover"
                                icon={mdiDelete}
                                onClick={() =>
                                    onChange(value.filter((_, i) => i !== indice))
                                }
                            />
                        </HBox>
                    </div>
                </div>
            ))}

            <VBox gap={8}>
                <Botao
                    variant="outline-primary"
                    title="Adicionar campo"
                    icon={mdiPlusCircleOutline}
                    onClick={() =>
                        onChange([
                            ...value,
                            { chave: '', rotulo: '', tipo: 'string' },
                        ])
                    }
                >
                    Adicionar campo
                </Botao>
            </VBox>
        </div>
    );
};

export default EditorCampos;
